import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
export type SelectDropdownOption = {
  value: string;
  label: string;
};
export interface SelectDropdownProps {
  value: string;
  options: SelectDropdownOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  menuAlign?: 'left' | 'right';
  icon?: React.ReactNode;
  scrollMenu?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /**
   * Optional custom trigger. Receives the open state so the caller can style
   * it, while this component keeps owning the menu, click-outside, and Escape
   * handling. Omit for the standard bordered trigger.
   */
  renderTrigger?: (state: {
    isOpen: boolean;
  }) => React.ReactNode;
  /** Menu width when a custom trigger is narrower than the options. */
  menuWidthClass?: string;
}
export function SelectDropdown({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'md',
  fullWidth = false,
  menuAlign = 'left',
  icon,
  scrollMenu = false,
  invalid = false,
  describedBy,
  renderTrigger,
  menuWidthClass
}: SelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(option => option.value === value);
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
  const triggerSizeClass = size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm';
  return <div ref={containerRef} className={`relative ${fullWidth ? 'w-full' : 'inline-block'}`} data-id="element-2104" data-mp="1rq92cfv1fxhc">
      <button type="button" onClick={() => setIsOpen(open => !open)} aria-haspopup="listbox" aria-expanded={isOpen} aria-label={ariaLabel} aria-invalid={invalid || undefined} aria-describedby={describedBy} className={renderTrigger ? 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#30718D]' : `flex ${fullWidth ? 'w-full' : 'min-w-[8.5rem]'} items-center justify-between gap-2 rounded-lg border bg-white ${triggerSizeClass} font-medium text-gray-900 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 ${invalid ? 'border-[#DB3725] focus:ring-[#DB3725]/30' : `focus:ring-[#30718D] ${isOpen ? 'border-[#30718D]' : 'border-gray-300'}`}`} data-id="element-2105" data-mp="1wq56cf3qjoww">
        {renderTrigger ? renderTrigger({
        isOpen
      }) : <>
            <span className="flex min-w-0 items-center gap-2" data-id="element-2106" data-mp="mrm9pyvos8u2">
              {icon && <span className="flex-none text-gray-400" aria-hidden="true" data-id="element-2107" data-mp="hssvg48ksdsu">
                  {icon}
                </span>}
              <span className="truncate text-left" data-id="element-2108" data-mp="wiqmqt1sxjm9">
                {selectedOption?.label || 'Select'}
              </span>
            </span>
            <ChevronDown size={16} aria-hidden="true" className={`flex-none text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-2109" data-mp="rovydcb94ggu" />
          </>}
      </button>

      {isOpen && <div role="listbox" aria-label={ariaLabel} className={`absolute top-full z-30 mt-2 ${menuWidthClass || (fullWidth ? 'w-full' : 'min-w-full')} rounded-lg border border-gray-200 bg-white shadow-lg ${scrollMenu ? 'max-h-72 overflow-y-auto' : 'overflow-hidden'} ${menuAlign === 'right' ? 'right-0' : 'left-0'}`} data-id="element-2110" data-mp="1ffi4uz5lxsoq">
          {options.map((option, index) => <button key={option.value} type="button" role="option" aria-selected={option.value === value} onClick={() => {
        onChange(option.value);
        setIsOpen(false);
      }} className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${index < options.length - 1 ? 'border-b border-gray-100' : ''} ${option.value === value ? 'bg-[#30718D]/5 font-semibold text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2111" data-mp="3doyvba0miknv" data-mpi={index}>
              {option.label}
            </button>)}
        </div>}
    </div>;
}